/**
 * The small set of components all three rooms need.
 *
 * Deliberately thin. A shared component library that grows ahead of real
 * demand becomes the thing every app fights; everything here is used by at
 * least two of the three apps today. Anything one app needs — a number pad on
 * the till, a report table in the back office — lives in that app.
 *
 * None of these declare a size. They read the room's tokens, so the same
 * `<Button variant="primary" size="lg">` is 52px in the back office and 88px
 * on the pass without either app asking for it.
 */

import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';

import './primitives.css';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant | undefined;
  /** sm/md/lg map to the room's three hit targets, not to fixed pixels. */
  size?: 'sm' | 'md' | 'lg' | undefined;
  loading?: boolean | undefined;
  /** Stretch to the width of its container. */
  block?: boolean | undefined;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  block = false,
  disabled,
  children,
  className = '',
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      className={`ui-btn ui-btn--${variant} ui-btn--${size}${block ? ' ui-btn--block' : ''} ${className}`.trim()}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {/* The label stays put while it works. Swapping it for "Working…"
          changes the width of a control someone may be about to tap again,
          and on a till that is how a double charge starts. */}
      {loading && <span className="ui-btn__spin" aria-hidden="true" />}
      <span className="ui-btn__label">{children}</span>
    </button>
  );
}

export function Card({
  title,
  actions,
  subdued = false,
  children,
  className = '',
  ...rest
}: HTMLAttributes<HTMLDivElement> & {
  title?: ReactNode | undefined;
  actions?: ReactNode | undefined;
  /** Sits on the page rather than above it. For grouping, not for emphasis. */
  subdued?: boolean | undefined;
}) {
  return (
    <section
      className={`ui-card${subdued ? ' ui-card--subdued' : ''} ${className}`.trim()}
      {...rest}
    >
      {(title || actions) && (
        <header className="ui-card__head">
          {title && <h2 className="ui-card__title">{title}</h2>}
          {actions && <div className="ui-card__actions">{actions}</div>}
        </header>
      )}
      <div className="ui-card__body">{children}</div>
    </section>
  );
}

export type Tone = 'neutral' | 'ok' | 'warn' | 'danger' | 'accent';

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: Tone | undefined;
  children: ReactNode;
}) {
  return <span className={`ui-badge ui-badge--${tone}`}>{children}</span>;
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label className="ui-field">
      <span className="ui-field__label">{label}</span>
      {children}
      {/* Error replaces the hint rather than stacking under it — two lines of
          small text below an input is where people stop reading. */}
      {error ? (
        <span className="ui-field__error" role="alert">
          {error}
        </span>
      ) : (
        hint && <span className="ui-field__hint">{hint}</span>
      )}
    </label>
  );
}

// `ComponentPropsWithRef` rather than the attributes alone: the sign-in code
// step has to let go of the field and take it again to change which keyboard
// iOS is showing, and it cannot do that without a handle on the element.
export function Input({ className = '', ...props }: React.ComponentPropsWithRef<'input'>) {
  return <input className={`ui-input ${className}`.trim()} {...props} />;
}

/**
 * A row of mutually exclusive choices.
 *
 * All three rooms have this shape — menu categories, kitchen stations, report
 * ranges, tender type — and all three had built their own, at three different
 * sizes, with the selected state carried by colour alone. Here it is one
 * control that reads the room's hit target and marks the selection with
 * `aria-pressed` as well as with fill.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  scroll = false,
}: {
  options: { value: T; label: ReactNode; badge?: ReactNode | undefined }[];
  value: T | null;
  onChange: (value: T) => void;
  /** Names the group for anyone not looking at it. */
  label: string;
  /** Let the strip scroll sideways when there are more than fit. */
  scroll?: boolean | undefined;
}) {
  return (
    <div className={`ui-seg${scroll ? ' ui-seg--scroll' : ''}`} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`ui-seg__opt${option.value === value ? ' is-on' : ''}`}
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          <span>{option.label}</span>
          {option.badge !== undefined && option.badge !== null && (
            <span className="ui-seg__badge">{option.badge}</span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * A sheet that rises from the bottom edge, with its actions pinned to it.
 *
 * Every room is a tablet, and on a tablet the on-screen keyboard eats the
 * bottom half of the viewport. A dialog centred in the page puts its submit
 * button underneath that keyboard; a sheet anchored to the bottom with a
 * sticky action bar keeps the button in the thumb's reach whether the keyboard
 * is up or not. It is also simply where a thumb already is.
 *
 * The body scrolls; the header and the actions do not.
 */
export function Sheet({
  title,
  onDismiss,
  actions,
  tone = 'neutral',
  size = 'normal',
  children,
}: {
  title: ReactNode;
  onDismiss: () => void;
  /** Null when there is nothing to do here — see the note on the footer. */
  actions?: ReactNode | undefined;
  /** `danger` for anything that destroys or takes money. */
  tone?: 'neutral' | 'danger' | undefined;
  /**
   * `wide` for a long form on a screen with room for it: the sheet widens and
   * its plain fields sit two to a row, so it is half as tall. Everything else
   * in it — lists, choices, notes — keeps the full width. `large` is the
   * same width with no columns, for a sheet that lays itself out.
   */
  size?: 'normal' | 'wide' | 'large' | undefined;
  children: ReactNode;
}) {
  return (
    <div className="ui-sheet-layer">
      {/* A scrim you can tap to leave, and that stops taps reaching the screen
          behind — on a till, a stray tap on the menu underneath an open dialog
          adds a dish nobody ordered. */}
      <button
        type="button"
        className="ui-sheet__scrim"
        aria-label="Dismiss"
        onClick={onDismiss}
      />
      <section
        className={`ui-sheet ui-sheet--${tone}${size === 'normal' ? '' : ' ui-sheet--wide'}`}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
      >
        <header className="ui-sheet__head">
          <h2 className="ui-sheet__title">{title}</h2>
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            Close
          </Button>
        </header>
        <div className="ui-sheet__body">
          {/* A wide sheet flows its form into two balanced columns inside the
              part that scrolls, so the columns are as tall as the form rather
              than as tall as the screen. */}
          {size === 'wide' ? <div className="ui-sheet__cols">{children}</div> : children}
        </div>
        {/* No footer at all when there is nothing to do, rather than an empty
            grey bar where the buttons should be. A control strip with no
            controls in it reads as something that failed to load. */}
        {actions != null && actions !== false && (
          <footer className="ui-sheet__foot">{actions}</footer>
        )}
      </section>
    </div>
  );
}

const PAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;

/**
 * A keypad, so the on-screen keyboard never opens.
 *
 * Both rooms that type numbers type them badly. A waiter enters £20.00 while
 * standing, with a guest waiting; a chef enters 2.5 in a walk-in holding the
 * tablet in one hand. The soft keyboard is the wrong tool for both: it eats
 * half the viewport, it puts the decimal point behind a shift layer, and it
 * offers a QWERTY grid to someone entering four digits.
 *
 * The value is a string throughout, never a number. Money that goes through
 * `parseFloat` and back is money that has been rounded by the browser, and the
 * server is the only thing allowed to do arithmetic on it.
 */
export function NumberPad({
  value,
  onChange,
  decimals = 2,
  label = 'Amount',
}: {
  value: string;
  onChange: (next: string) => void;
  /** 0 for whole numbers. Money is 2; a stock count may want more. */
  decimals?: number | undefined;
  label?: string | undefined;
}) {
  function press(key: string) {
    if (key === '.') {
      if (decimals === 0 || value.includes('.')) return;
      onChange(value === '' ? '0.' : `${value}.`);
      return;
    }
    const [, fraction] = value.split('.');
    if (fraction !== undefined && fraction.length >= decimals) return;
    // No leading zeros: "05" is a typo every time, never an amount.
    onChange(value === '0' ? key : value + key);
  }

  return (
    <div className="ui-pad" role="group" aria-label={label}>
      {PAD_KEYS.map((key) => (
        <button key={key} type="button" className="ui-pad__key" onClick={() => press(key)}>
          {key}
        </button>
      ))}
      <button
        type="button"
        className="ui-pad__key ui-pad__key--quiet"
        onClick={() => press('.')}
        disabled={decimals === 0}
        aria-label="Decimal point"
      >
        .
      </button>
      <button type="button" className="ui-pad__key" onClick={() => press('0')}>
        0
      </button>
      <button
        type="button"
        className="ui-pad__key ui-pad__key--quiet"
        onClick={() => onChange(value.slice(0, -1))}
        disabled={value === ''}
        aria-label="Delete the last digit"
      >
        <span aria-hidden="true">⌫</span>
      </button>
    </div>
  );
}

/** Full-page state while the shell resolves who you are. */
export function Loading({ label = 'Loading' }: { label?: string | undefined }) {
  return (
    <div className="ui-center" role="status" aria-live="polite">
      <span className="ui-spinner" aria-hidden="true" />
      <span className="ui-muted">{label}…</span>
    </div>
  );
}

/**
 * Empty state.
 *
 * Says what is missing and what to do about it. "No data" tells the user
 * nothing they did not already know from looking at the screen.
 */
export function Empty({
  title,
  description,
  action,
}: {
  title: string;
  description?: string | undefined;
  action?: ReactNode | undefined;
}) {
  return (
    <div className="ui-empty">
      <p className="ui-empty__title">{title}</p>
      {description && <p className="ui-empty__desc">{description}</p>}
      {action}
    </div>
  );
}

export function ErrorNotice({
  title = 'Something went wrong',
  message,
  action,
}: {
  title?: string | undefined;
  message: string;
  action?: ReactNode | undefined;
}) {
  return (
    <div className="ui-notice ui-notice--danger" role="alert">
      <p className="ui-notice__title">{title}</p>
      <p className="ui-notice__body">{message}</p>
      {action}
    </div>
  );
}

/**
 * A figure that was deliberately not shown, and the reason, in the open.
 *
 * This replaces a `title=` tooltip, which on a tablet is unreachable: there is
 * no hover, so the explanation for a dash simply did not exist for the people
 * most likely to need it. An absence and a zero are different facts, and the
 * difference has to be legible without a mouse.
 */
export function Withheld({ reason }: { reason?: string | undefined }) {
  return (
    <span className="ui-withheld">
      <span aria-hidden="true">—</span>
      <span className="sr-only">{reason ?? 'Not shown'}</span>
    </span>
  );
}
